import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

function getWorkerInfo() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { role: 'project_manager', name: '' };
    const p = JSON.parse(decodeURIComponent(escape(atob(token.split('.')[1]))));
    return { role: p.role || 'project_manager', name: `${p.workerFirstName || ''} ${p.workerLastName || ''}`.trim(), userName: p.userName || '' };
  } catch { return { role: 'project_manager', name: '', userName: '' }; }
}

function avatarTransform(pos, zoom) {
  const [x, y] = (!pos || pos === '50% 50%' ? '0% 0%' : pos).replace(/%/g, '').split(' ').map(Number);
  return `translate(${isNaN(x) ? 0 : x}%, ${isNaN(y) ? 0 : y}%) scale(${zoom || 1})`;
}

async function downloadCrossOriginFile(url, filename) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename || 'file';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch {
    window.open(url, '_blank');
  }
}

function DragReposition({ repositioning, setRepositioning, onSave, onCancel }) {
  const boxRef = React.useRef(null);
  const dragState = React.useRef(null); // { startX, startY, posX, posY }

  function onPointerDown(e) {
    e.preventDefault();
    const box = boxRef.current;
    if (!box) return;
    box.setPointerCapture(e.pointerId);
    document.body.style.userSelect = 'none';
    dragState.current = {
      startX: e.clientX, startY: e.clientY,
      posX: repositioning.x, posY: repositioning.y,
      w: box.clientWidth, h: box.clientHeight,
    };
  }
  function onPointerMove(e) {
    const d = dragState.current;
    if (!d) return;
    e.preventDefault();
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    const newX = Math.max(-50, Math.min(50, d.posX + (dx / d.w) * 100));
    const newY = Math.max(-50, Math.min(50, d.posY + (dy / d.h) * 100));
    setRepositioning(r => ({ ...r, x: newX, y: newY }));
  }
  function onPointerUp(e) {
    dragState.current = null;
    document.body.style.userSelect = '';
    boxRef.current?.releasePointerCapture(e.pointerId);
  }
  function onWheel(e) {
    e.preventDefault();
    const zoom = repositioning.zoom || 1;
    const next = Math.max(0.4, Math.min(3, zoom - e.deltaY * 0.001));
    setRepositioning(r => ({ ...r, zoom: next }));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '8px 0' }}>
      <div
        ref={boxRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        style={{
          width: 160, height: 160, borderRadius: '50%', overflow: 'hidden',
          border: '2px solid #e5e7eb', background: '#f9fafb',
          cursor: 'grab', touchAction: 'none', userSelect: 'none',
        }}
      >
        <img
          src={repositioning.avatar_url}
          alt=""
          draggable={false}
          onDragStart={e => e.preventDefault()}
          style={{
            width: '100%', height: '100%', objectFit: 'contain',
            transform: `translate(${repositioning.x}%, ${repositioning.y}%) scale(${repositioning.zoom || 1})`,
            pointerEvents: 'none', WebkitUserDrag: 'none',
          }}
        />
      </div>
      <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>גררי כדי למקם, גלגלת עכבר כדי להגדיל/להקטין</div>
      <div style={{ display: 'flex', gap: 8, width: '100%' }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>ביטול</button>
        <button onClick={onSave} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: '#1d4ed8', color: 'white', fontWeight: 600, cursor: 'pointer' }}>שמור</button>
      </div>
    </div>
  );
}

const SOCIAL_LINK_TYPES = [
  { type: 'website', label: '🌐 אתר' },
  { type: 'whatsapp', label: '💬 וואטסאפ' },
  { type: 'facebook', label: '📘 פייסבוק' },
  { type: 'instagram', label: '📸 אינסטגרם' },
];

const PreviewIconPhone = ({ size = 16, color = '#ef4444' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
);
const PreviewIconEmail = ({ size = 16, color = '#1f2937' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M2 6l10 7 10-7" />
  </svg>
);
const PreviewIconWebsite = ({ size = 16, color = '#1f2937' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20" />
  </svg>
);
const PreviewIconFacebook = ({ size = 16, color = '#1877F2' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const PreviewIconInstagram = ({ size = 16 }) => {
  const gid = 'ig-grad-preview-' + Math.random().toString(36).slice(2);
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <defs><linearGradient id={gid} x1="0" y1="1" x2="1" y2="0">
        <stop offset="0%" stopColor="#FEDA75" /><stop offset="25%" stopColor="#FA7E1E" /><stop offset="50%" stopColor="#D62976" /><stop offset="75%" stopColor="#962FBF" /><stop offset="100%" stopColor="#4F5BD5" />
      </linearGradient></defs>
      <path fill={`url(#${gid})`} d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
};
const PreviewIconAddContact = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="12" fill="#ffffff" fillOpacity="0.22" />
    <circle cx="12" cy="10" r="3" fill="#ffffff" />
    <path d="M6.2 18.2c0-2.6 2.6-4.2 5.8-4.2s5.8 1.6 5.8 4.2" fill="#ffffff" />
  </svg>
);
const PreviewIconWhatsApp = ({ size = 16, color = '#25D366' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);
const PREVIEW_SOCIAL_ICONS = {
  website: <PreviewIconWebsite />, whatsapp: <PreviewIconWhatsApp />, facebook: <PreviewIconFacebook />, instagram: <PreviewIconInstagram />,
};
const PREVIEW_SOCIAL_LABELS = {
  website: 'אתר', whatsapp: 'וואטסאפ', facebook: 'פייסבוק', instagram: 'אינסטגרם',
};
// Community Group's own brand colors (from the logo: teal mountain, orange sunset).
const COMMUNITY_THEME = { primary: '#1F8A8C', accent: '#F5941F' };
function WorkerCardPreviewModal({ card, form, onClose }) {
  const fullName = `${card.workerFirstName || ''} ${card.workerLastName || ''}`.trim();
  const links = Object.entries(form.social_links).filter(([, url]) => url).map(([type, url]) => ({ type, url }));
  const linkRow = { display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0px 13px 16px', borderRadius: 14, background: '#f8fafc', color: '#1f2937', fontSize: '0.9rem', border: '1px solid #eef1f5' };
  const iconBubble = { width: 30, height: 30, borderRadius: '50%', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', flexShrink: 0 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 400, borderRadius: 28, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', background: 'white', direction: 'rtl' }}>
        <div style={{ background: `linear-gradient(225deg,${COMMUNITY_THEME.primary},${COMMUNITY_THEME.accent})`, padding: '28px 24px 60px', textAlign: 'center', position: 'relative' }}>
          <div style={{
            width: 128, height: 128, borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6',
            position: 'absolute', left: '50%', bottom: -64, transform: 'translateX(-50%)',
            border: '4px solid white', boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
          }}>
            {card.avatar_url
              ? <img src={card.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: avatarTransform(card.avatar_pos, card.avatar_zoom) }} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>👤</div>}
          </div>
        </div>
        <div style={{ padding: '76px 28px 32px', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '1.35rem', color: '#1f2937' }}>{fullName}</div>
          {form.display_title && <div style={{ fontSize: '0.9rem', color: COMMUNITY_THEME.accent, fontWeight: 600, marginTop: 4 }}>{form.display_title}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 26 }}>
            {form.card_phone && <div style={linkRow}><span dir="ltr">{form.card_phone}</span> <span style={{ ...iconBubble, marginInlineStart: 'auto' }}><PreviewIconPhone /></span></div>}
            {form.card_email && <div style={linkRow}><span dir="ltr">{form.card_email}</span> <span style={{ ...iconBubble, marginInlineStart: 'auto' }}><PreviewIconEmail /></span></div>}
            {links.map(l => (
              <div key={l.type} style={linkRow}><span>{PREVIEW_SOCIAL_LABELS[l.type] || l.type}</span> <span style={{ ...iconBubble, marginInlineStart: 'auto' }}>{PREVIEW_SOCIAL_ICONS[l.type] || '🔗'}</span></div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 24, padding: '14px 0', borderRadius: 14, background: `linear-gradient(225deg,${COMMUNITY_THEME.primary},${COMMUNITY_THEME.accent})`, color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
            <PreviewIconAddContact size={18} /> הוסף לאנשי קשר
          </div>
        </div>
      </div>
      <button onClick={onClose} style={{ position: 'absolute', top: 20, left: 20, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: '1.4rem', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer' }}>✕</button>
    </div>
  );
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Draws photo + name + title + QR into one PNG (canvas composite) so sharing
// the QR as a photo also carries who it belongs to, instead of a bare
// barcode. avatarPos/avatarZoom replicate the same drag/zoom transform used
// on-screen (avatarTransform: translate(x%,y%) then scale(zoom), relative to
// the avatar's own box, matching object-fit:contain there).
// The Web Share API's "share to app" picker is a real, working feature on
// Android/iOS, but on desktop Windows/Chrome it can report canShare()=true
// and then have the OS's own native share sheet fail with a generic
// "couldn't show you all the ways to share" error (no WhatsApp desktop share
// target registered) — a platform limitation, not something the page can fix.
// Restrict the native-share attempt to actual mobile devices, and always
// have a working download fallback for everyone else.
function isMobileDevice() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
async function shareOrDownloadImage(blob, filename) {
  const file = new File([blob], filename, { type: 'image/png' });
  if (isMobileDevice() && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return;
    } catch {
      // user cancelled, or the native sheet failed anyway — fall through to download
    }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  alert('השיתוף הישיר לוואטסאפ זמין רק בטלפון. התמונה הורדה — אפשר לצרף אותה ידנית בוואטסאפ.');
}

async function buildQrShareImage(qrUrl, name, title, avatarUrl, avatarPos, avatarZoom) {
  const qrImg = await loadImage(qrUrl);
  const W = 360, QR = 280, PAD = 24, AV = 100;
  // Loaded through our own server (same-origin) instead of directly from S3
  // — a canvas-drawn cross-origin image needs CORS headers on every request,
  // and that proved unreliable straight off S3, silently dropping the avatar.
  const avatarImg = avatarUrl ? await loadImage(`/api/portal/image-proxy?url=${encodeURIComponent(avatarUrl)}`).catch(() => null) : null;
  const headerH = 20 + (avatarImg ? AV + 12 : 0) + 30 + (title ? 24 : 0) + 10;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = headerH + QR + PAD * 2;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let y = 20;
  if (avatarImg) {
    const cx = W / 2, cy = y + AV / 2;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, AV / 2, 0, Math.PI * 2);
    ctx.clip();
    const scale0 = Math.min(AV / avatarImg.width, AV / avatarImg.height);
    const drawW = avatarImg.width * scale0, drawH = avatarImg.height * scale0;
    const [px, py] = (!avatarPos || avatarPos === '50% 50%' ? '0% 0%' : avatarPos).replace(/%/g, '').split(' ').map(Number);
    const zoom = avatarZoom ? Number(avatarZoom) : 1;
    ctx.translate(cx + (isNaN(px) ? 0 : px) / 100 * AV, cy + (isNaN(py) ? 0 : py) / 100 * AV);
    ctx.scale(zoom, zoom);
    ctx.drawImage(avatarImg, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
    y += AV + 12;
  }

  ctx.direction = 'rtl';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#1f2937';
  ctx.font = "700 22px 'Heebo', Arial, sans-serif";
  ctx.fillText(name || '', W / 2, y + 20);
  y += 30;
  if (title) {
    ctx.fillStyle = '#1d4ed8';
    ctx.font = "600 15px 'Heebo', Arial, sans-serif";
    ctx.fillText(title, W / 2, y + 12);
    y += 24;
  }
  ctx.drawImage(qrImg, (W - QR) / 2, y + 10, QR, QR);

  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/png');
  });
}

const CARD_TAB_STORAGE_KEY = 'digitalCardTab_selection';
function HeaderRow({ onBack }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#6b7280' }}>📇 כרטיס ביקור דיגיטלי — גרסת ניסיון</div>
      <button className="btn btn-secondary" onClick={onBack} style={{ whiteSpace: 'nowrap' }}>חזרה ←</button>
    </div>
  );
}

function DigitalCardEditorInner({ projectName, onBack }) {
  const self = getWorkerInfo();
  const isAdmin = self.role === 'admin';
  const [workers, setWorkers] = useState([]);
  // Remember which worker/company was being edited across a page refresh —
  // otherwise the picker always resets to "yourself", which is disorienting
  // for an admin who was mid-edit on someone else's card.
  const saved = (() => { try { return JSON.parse(localStorage.getItem(CARD_TAB_STORAGE_KEY)); } catch { return null; } })();
  const [mode, setMode] = useState(saved?.mode === 'company' || saved?.mode === 'custom' ? saved.mode : 'worker');
  const [targetUserName, setTargetUserName] = useState(saved?.mode === 'worker' && saved?.targetUserName ? saved.targetUserName : self.userName);
  const [card, setCard] = useState(null);
  const [form, setForm] = useState({ display_title: '', social_links: {} });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [repositioning, setRepositioning] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);
  const fileRef = React.useRef();

  // Company card — same "digital business card" concept, one per project.
  // Name/photo/logo stay editable in "אודות המנהלת"; this only manages the
  // contact-card-specific fields (phone/email/links) and the public link.
  const [company, setCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ phone: '', email: '', social_links: {} });
  const [companySaving, setCompanySaving] = useState(false);
  const [companyToggling, setCompanyToggling] = useState(false);
  const [companyLinkCopied, setCompanyLinkCopied] = useState(false);
  const [companyQrCopied, setCompanyQrCopied] = useState(false);

  // Standalone cards for people with no Worker login at all (e.g. sales reps,
  // external contacts) — a separate list the admin can create into freely.
  const [customCards, setCustomCards] = useState([]);
  const [customCardId, setCustomCardId] = useState(saved?.mode === 'custom' ? saved?.customCardId || null : null);
  const [customCard, setCustomCard] = useState(null);
  const [customForm, setCustomForm] = useState({ name: '', title: '', phone: '', email: '', social_links: {} });
  const [customSaving, setCustomSaving] = useState(false);
  const [customToggling, setCustomToggling] = useState(false);
  const [customLinkCopied, setCustomLinkCopied] = useState(false);
  const [customQrCopied, setCustomQrCopied] = useState(false);
  const [customRepositioning, setCustomRepositioning] = useState(null);
  const [customPreviewing, setCustomPreviewing] = useState(false);
  const [newCardDraft, setNewCardDraft] = useState(null); // { type, name } while the "new card" modal is open

  useEffect(() => {
    if (isAdmin) api.get('/portal/workers').then(r => setWorkers(r.data || [])).catch(() => {});
    api.get('/portal/custom-cards').then(r => setCustomCards(r.data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    try { localStorage.setItem(CARD_TAB_STORAGE_KEY, JSON.stringify({ mode, targetUserName, customCardId })); } catch {}
  }, [mode, targetUserName, customCardId]);

  const load = useCallback(() => {
    setCard(null);
    api.get('/portal/worker-card/me', { params: { userName: targetUserName } }).then(r => {
      setCard(r.data);
      const links = {};
      (r.data.social_links || []).forEach(l => { links[l.type] = l.url; });
      setForm({ display_title: r.data.display_title || '', card_phone: r.data.card_phone || '', card_email: r.data.card_email || '', social_links: links });
    }).catch(() => {});
  }, [targetUserName]);

  useEffect(() => { if (mode === 'worker') load(); }, [mode, load]);

  const loadCompany = useCallback(() => {
    setCompany(null);
    api.get(`/portal/about/manhelet/${encodeURIComponent(projectName)}`).then(r => {
      const data = r.data || {};
      setCompany(data);
      const links = {};
      (data.social_links || []).forEach(l => { links[l.type] = l.url; });
      setCompanyForm({
        manhelet_name: data.manhelet_name || '', title: data.title || '',
        logo_url: data.logo_url || '', phone: data.phone || '', email: data.email || '', social_links: links,
      });
    }).catch(() => {});
  }, [projectName]);

  useEffect(() => { if (mode === 'company') loadCompany(); }, [mode, loadCompany]);

  async function saveCompany() {
    setCompanySaving(true);
    const social_links = Object.entries(companyForm.social_links).filter(([, url]) => url).map(([type, url]) => ({ type, url }));
    await Promise.all([
      api.post(`/portal/about/manhelet/${encodeURIComponent(projectName)}`, {
        manhelet_name: companyForm.manhelet_name, title: companyForm.title, description: company.description,
        photo_url: company.photo_url, photo_position: company.photo_position, photo_zoom: company.photo_zoom,
        logo_url: companyForm.logo_url,
      }).catch(() => {}),
      api.put(`/portal/manhelet-card/${encodeURIComponent(projectName)}`, { phone: companyForm.phone, email: companyForm.email, social_links }).catch(() => {}),
    ]);
    setCompanySaving(false);
    loadCompany();
  }
  async function toggleCompanyEnabled(enabled) {
    setCompanyToggling(true);
    await api.patch(`/portal/manhelet-card/${encodeURIComponent(projectName)}/enabled`, { enabled }).catch(() => {});
    setCompanyToggling(false);
    loadCompany();
  }
  async function uploadCompanyLogo(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    const r = await api.post('/portal/about/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setCompanyForm(f => ({ ...f, logo_url: r.data.url }));
  }

  const loadCustomCard = useCallback(() => {
    if (!customCardId) return;
    setCustomCard(null);
    api.get(`/portal/custom-card/${customCardId}`).then(r => {
      const data = r.data || {};
      setCustomCard(data);
      const links = {};
      (data.social_links || []).forEach(l => { links[l.type] = l.url; });
      setCustomForm({ name: data.name || '', title: data.title || '', phone: data.phone || '', email: data.email || '', social_links: links });
    }).catch(() => {});
  }, [customCardId]);

  useEffect(() => { if (mode === 'custom' && customCardId) loadCustomCard(); }, [mode, customCardId, loadCustomCard]);

  async function submitNewCard() {
    const name = newCardDraft.name.trim();
    if (!name) return;
    const r = await api.post('/portal/custom-cards', { name, created_by: self.userName, type: newCardDraft.type }).catch(() => null);
    if (!r) return;
    setCustomCards(cs => [...cs, { id: r.data.id, name, type: newCardDraft.type }]);
    setMode('custom');
    setCustomCardId(r.data.id);
    setNewCardDraft(null);
  }

  async function saveCustomCard() {
    setCustomSaving(true);
    const social_links = Object.entries(customForm.social_links).filter(([, url]) => url).map(([type, url]) => ({ type, url }));
    await api.put(`/portal/custom-card/${customCardId}`, {
      name: customForm.name, title: customForm.title, phone: customForm.phone, email: customForm.email, social_links,
    }).catch(() => {});
    setCustomCards(cs => cs.map(c => c.id === customCardId ? { ...c, name: customForm.name } : c));
    setCustomSaving(false);
    loadCustomCard();
  }
  async function toggleCustomEnabled(enabled) {
    setCustomToggling(true);
    await api.patch(`/portal/custom-card/${customCardId}/enabled`, { enabled }).catch(() => {});
    setCustomToggling(false);
    loadCustomCard();
  }
  async function uploadCustomAvatar(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    await api.post(`/portal/custom-card/${customCardId}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => {});
    loadCustomCard();
  }
  async function saveCustomReposition() {
    await api.patch(`/portal/custom-card/${customCardId}/avatar-pos`, {
      pos: `${Math.round(customRepositioning.x)}% ${Math.round(customRepositioning.y)}%`, zoom: customRepositioning.zoom,
    }).catch(() => {});
    setCustomRepositioning(null);
    loadCustomCard();
  }
  async function save() {
    setSaving(true);
    const social_links = Object.entries(form.social_links).filter(([, url]) => url).map(([type, url]) => ({ type, url }));
    await api.put('/portal/worker-card/me', { userName: targetUserName, display_title: form.display_title, card_phone: form.card_phone, card_email: form.card_email, social_links }).catch(() => {});
    setSaving(false);
    load();
  }

  async function toggleEnabled(enabled) {
    setToggling(true);
    await api.patch('/portal/worker-card/me/enabled', { userName: targetUserName, enabled }).catch(() => {});
    setToggling(false);
    load();
  }

  async function uploadAvatar(file) {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('userName', targetUserName);
    await api.post('/portal/worker-card/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => {});
    load();
  }

  async function saveReposition() {
    // Drag produces fractional %s (e.g. -12.837), but the server's pos regex
    // only accepts whole numbers — round before sending or the PATCH 400s.
    await api.patch('/portal/worker-card/me/avatar-pos', {
      userName: targetUserName, pos: `${Math.round(repositioning.x)}% ${Math.round(repositioning.y)}%`, zoom: repositioning.zoom,
    }).catch(() => {});
    setRepositioning(null);
    load();
  }

  const cardStyle = { background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', marginBottom: 16, maxWidth: 480 };
  const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.9rem', marginBottom: 8, boxSizing: 'border-box' };

  const picker = (
    <div style={{ ...cardStyle, padding: '14px 20px' }}>
      <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 5 }}>עריכת כרטיס עבור</label>
      <select value={mode === 'company' ? '__company__' : mode === 'custom' ? `__custom_${customCardId}` : targetUserName}
        onChange={e => {
          const v = e.target.value;
          if (v === '__company__') setMode('company');
          else if (v === '__create_custom__') setNewCardDraft({ type: 'person', name: '' });
          else if (v.startsWith('__custom_')) { setMode('custom'); setCustomCardId(Number(v.replace('__custom_', ''))); }
          else { setMode('worker'); setTargetUserName(v); }
        }}
        style={{ ...inp, marginBottom: 0, cursor: 'pointer' }}>
        <option value={self.userName}>{self.name} (את/ה)</option>
        {isAdmin && workers.filter(w => w.userName !== self.userName).map(w => (
          <option key={w.userName} value={w.userName}>{w.workerFirstName} {w.workerLastName}</option>
        ))}
        <option value="__company__">🏢 כרטיס חברה</option>
        {customCards.map(c => (
          <option key={c.id} value={`__custom_${c.id}`}>{c.type === 'company' ? '🏢' : '👤'} {c.name}</option>
        ))}
        <option value="__create_custom__">➕ צור כרטיס חדש...</option>
      </select>

      {newCardDraft && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setNewCardDraft(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 24, width: 320 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1f2937', marginBottom: 14 }}>צור כרטיס ביקור חדש</div>
            <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 6 }}>סוג הכרטיס</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button onClick={() => setNewCardDraft(d => ({ ...d, type: 'person' }))}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', border: newCardDraft.type === 'person' ? '2px solid #1d4ed8' : '1px solid #d1d5db', background: newCardDraft.type === 'person' ? '#eff6ff' : 'white', color: newCardDraft.type === 'person' ? '#1d4ed8' : '#374151' }}>
                👤 איש קשר
              </button>
              <button onClick={() => setNewCardDraft(d => ({ ...d, type: 'company' }))}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', border: newCardDraft.type === 'company' ? '2px solid #1d4ed8' : '1px solid #d1d5db', background: newCardDraft.type === 'company' ? '#eff6ff' : 'white', color: newCardDraft.type === 'company' ? '#1d4ed8' : '#374151' }}>
                🏢 חברה
              </button>
            </div>
            <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 6 }}>{newCardDraft.type === 'company' ? 'שם החברה' : 'שם מלא'}</label>
            <input autoFocus style={inp} value={newCardDraft.name} onChange={e => setNewCardDraft(d => ({ ...d, name: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') submitNewCard(); }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => setNewCardDraft(null)} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>ביטול</button>
              <button onClick={submitNewCard} disabled={!newCardDraft.name.trim()} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: '#1d4ed8', color: 'white', fontWeight: 600, cursor: 'pointer' }}>צור</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (mode === 'company') {
    if (!company) return <div><HeaderRow onBack={onBack} />{picker}<div style={{ color: '#9ca3af', padding: 20 }}>טוען...</div></div>;
    const companyUrl = company.card_slug ? `https://mabat.communityltd.co.il/card/company/${company.card_slug}` : null;
    return (
      <div>
        <HeaderRow onBack={onBack} />
        {picker}
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 110, height: 110, borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, boxSizing: 'border-box' }}>
              {companyForm.logo_url ? <img src={companyForm.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '2.2rem' }}>🏢</span>}
            </div>
            <label style={{ cursor: 'pointer', fontSize: '0.78rem', color: '#1d4ed8' }}>
              {companyForm.logo_url ? 'החלף לוגו' : '+ לוגו'}
              <input type="file" accept="image/*" hidden onChange={e => uploadCompanyLogo(e.target.files[0])} />
            </label>
          </div>

          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>שם החברה/מנהלת</label>
          <input style={inp} value={companyForm.manhelet_name} onChange={e => setCompanyForm(f => ({ ...f, manhelet_name: e.target.value }))} />
          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>תפקיד / תיאור קצר</label>
          <input style={inp} value={companyForm.title} onChange={e => setCompanyForm(f => ({ ...f, title: e.target.value }))} />
          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>טלפון</label>
          <input style={inp} value={companyForm.phone} onChange={e => setCompanyForm(f => ({ ...f, phone: e.target.value }))} />
          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>אימייל</label>
          <input style={inp} value={companyForm.email} onChange={e => setCompanyForm(f => ({ ...f, email: e.target.value }))} />

          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3, marginTop: 6 }}>קישורים</label>
          {SOCIAL_LINK_TYPES.map(({ type, label }) => (
            <input key={type} style={inp} placeholder={label} value={companyForm.social_links[type] || ''}
              onChange={e => setCompanyForm(f => ({ ...f, social_links: { ...f.social_links, [type]: e.target.value } }))} />
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={saveCompany} disabled={companySaving} style={{ fontSize: '0.85rem' }}>{companySaving ? 'שומר...' : 'שמור'}</button>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#6b7280', marginBottom: 10 }}>קישור ציבורי</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!company.card_enabled} disabled={companyToggling} onChange={e => toggleCompanyEnabled(e.target.checked)} />
            <span style={{ fontSize: '0.85rem' }}>הפעל כרטיס ציבורי (נגיש בלי התחברות)</span>
          </label>
          {company.card_enabled && companyUrl && (() => {
            const qrUrl = `https://mabat.communityltd.co.il/api/portal/card/company/${company.card_slug}/qr`;
            return (
              <>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                  <input readOnly value={companyUrl} style={{ ...inp, marginBottom: 0, direction: 'ltr', fontSize: '0.78rem', flex: 1, minWidth: 200 }} onFocus={e => e.target.select()} />
                  <button onClick={() => { navigator.clipboard.writeText(companyUrl); setCompanyLinkCopied(true); setTimeout(() => setCompanyLinkCopied(false), 2000); }}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: companyLinkCopied ? '#f0fdf4' : 'white', color: companyLinkCopied ? '#15803d' : '#374151', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {companyLinkCopied ? '✓ הועתק' : 'העתק'}
                  </button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(companyUrl)}`} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap', textDecoration: 'none' }}>
                    <PreviewIconWhatsApp size={14} /> שלח
                  </a>
                </div>
                <img src={qrUrl} alt="QR" style={{ width: 140, height: 140, display: 'block' }} />
                <button onClick={async () => {
                  try {
                    const blob = await buildQrShareImage(qrUrl, company.manhelet_name || '', company.title || '', company.logo_url, null, 1);
                    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                    setCompanyQrCopied(true); setTimeout(() => setCompanyQrCopied(false), 2000);
                  } catch {
                    try {
                      const blob = await buildQrShareImage(qrUrl, company.manhelet_name || '', company.title || '', company.logo_url, null, 1);
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `card-${company.manhelet_name || 'company'}.png`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    } catch {}
                  }
                }} style={{ marginTop: 8, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: companyQrCopied ? '#f0fdf4' : 'white', color: companyQrCopied ? '#15803d' : '#374151', cursor: 'pointer', fontSize: '0.78rem' }}>
                  {companyQrCopied ? '✓ הועתק' : '📋 העתק כרטיס (שם + QR)'}
                </button>{' '}
                <button onClick={async () => {
                  const blob = await buildQrShareImage(qrUrl, company.manhelet_name || '', company.title || '', company.logo_url, null, 1).catch(() => null);
                  if (!blob) return;
                  await shareOrDownloadImage(blob, `card-${company.manhelet_name || 'company'}.png`);
                }} style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.78rem' }}>
                  <PreviewIconWhatsApp size={14} /> שלח (עם QR)
                </button>
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  if (mode === 'custom') {
    if (!customCard) return <div><HeaderRow onBack={onBack} />{picker}<div style={{ color: '#9ca3af', padding: 20 }}>טוען...</div></div>;
    const customUrl = customCard.card_slug ? `https://mabat.communityltd.co.il/card/person/${customCard.card_slug}` : null;
    return (
      <div>
        <HeaderRow onBack={onBack} />
        {picker}
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 110, height: 110, borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', border: '1px solid #e5e7eb' }}>
              {customCard.avatar_url
                ? <img src={customCard.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: avatarTransform(customCard.avatar_pos, customCard.avatar_zoom) }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem' }}>👤</div>}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ cursor: 'pointer', fontSize: '0.78rem', color: '#1d4ed8' }}>
                {customCard.avatar_url ? 'החלף תמונה' : '+ תמונה'}
                <input type="file" accept="image/*" hidden onChange={e => { const f = e.target.files[0]; if (f) uploadCustomAvatar(f); }} />
              </label>
              {customCard.avatar_url && (
                <button onClick={() => {
                  const raw = customCard.avatar_pos && customCard.avatar_pos !== '50% 50%' ? customCard.avatar_pos : '0% 0%';
                  const [x, y] = raw.replace(/%/g, '').split(' ').map(Number);
                  setCustomRepositioning({ avatar_url: customCard.avatar_url, x: isNaN(x) ? 0 : x, y: isNaN(y) ? 0 : y, zoom: customCard.avatar_zoom ? Number(customCard.avatar_zoom) : 1 });
                }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#6b7280' }}>✏️ מיקום/זום</button>
              )}
            </div>
          </div>

          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>{customCard.type === 'company' ? 'שם החברה' : 'שם מלא'}</label>
          <input style={inp} value={customForm.name} onChange={e => setCustomForm(f => ({ ...f, name: e.target.value }))} />
          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>תפקיד לתצוגה</label>
          <input style={inp} value={customForm.title} onChange={e => setCustomForm(f => ({ ...f, title: e.target.value }))} />
          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>טלפון</label>
          <input style={inp} value={customForm.phone} onChange={e => setCustomForm(f => ({ ...f, phone: e.target.value }))} />
          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>אימייל</label>
          <input style={inp} value={customForm.email} onChange={e => setCustomForm(f => ({ ...f, email: e.target.value }))} />

          <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3, marginTop: 6 }}>קישורים</label>
          {SOCIAL_LINK_TYPES.map(({ type, label }) => (
            <input key={type} style={inp} placeholder={label} value={customForm.social_links[type] || ''}
              onChange={e => setCustomForm(f => ({ ...f, social_links: { ...f.social_links, [type]: e.target.value } }))} />
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={saveCustomCard} disabled={customSaving} style={{ fontSize: '0.85rem' }}>{customSaving ? 'שומר...' : 'שמור'}</button>
            <button className="btn btn-secondary" onClick={() => setCustomPreviewing(true)} style={{ fontSize: '0.85rem' }}>👁 תצוגה מקדימה</button>
          </div>
        </div>

        {customPreviewing && (
          <WorkerCardPreviewModal
            card={{ workerFirstName: customForm.name, workerLastName: '', avatar_url: customCard.avatar_url, avatar_pos: customCard.avatar_pos, avatar_zoom: customCard.avatar_zoom }}
            form={{ display_title: customForm.title, card_phone: customForm.phone, card_email: customForm.email, social_links: customForm.social_links }}
            onClose={() => setCustomPreviewing(false)}
          />
        )}

        {customRepositioning && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setCustomRepositioning(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 20, width: 260 }}>
              <DragReposition repositioning={customRepositioning} setRepositioning={setCustomRepositioning} onSave={saveCustomReposition} onCancel={() => setCustomRepositioning(null)} />
            </div>
          </div>
        )}

        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#6b7280', marginBottom: 10 }}>קישור ציבורי</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!customCard.card_enabled} disabled={customToggling} onChange={e => toggleCustomEnabled(e.target.checked)} />
            <span style={{ fontSize: '0.85rem' }}>הפעל כרטיס ציבורי (נגיש בלי התחברות)</span>
          </label>
          {customCard.card_enabled && customUrl && (() => {
            const qrUrl = `https://mabat.communityltd.co.il/api/portal/card/person/${customCard.card_slug}/qr`;
            return (
              <>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                  <input readOnly value={customUrl} style={{ ...inp, marginBottom: 0, direction: 'ltr', fontSize: '0.78rem', flex: 1, minWidth: 200 }} onFocus={e => e.target.select()} />
                  <button onClick={() => { navigator.clipboard.writeText(customUrl); setCustomLinkCopied(true); setTimeout(() => setCustomLinkCopied(false), 2000); }}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: customLinkCopied ? '#f0fdf4' : 'white', color: customLinkCopied ? '#15803d' : '#374151', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {customLinkCopied ? '✓ הועתק' : 'העתק'}
                  </button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(customUrl)}`} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap', textDecoration: 'none' }}>
                    <PreviewIconWhatsApp size={14} /> שלח
                  </a>
                </div>
                <img src={qrUrl} alt="QR" style={{ width: 140, height: 140, display: 'block' }} />
                <button onClick={async () => {
                  try {
                    const blob = await buildQrShareImage(qrUrl, customCard.name || '', customCard.title || '', customCard.avatar_url, customCard.avatar_pos, customCard.avatar_zoom);
                    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                    setCustomQrCopied(true); setTimeout(() => setCustomQrCopied(false), 2000);
                  } catch {
                    try {
                      const blob = await buildQrShareImage(qrUrl, customCard.name || '', customCard.title || '', customCard.avatar_url, customCard.avatar_pos, customCard.avatar_zoom);
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `card-${customCard.name || 'card'}.png`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    } catch {}
                  }
                }} style={{ marginTop: 8, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: customQrCopied ? '#f0fdf4' : 'white', color: customQrCopied ? '#15803d' : '#374151', cursor: 'pointer', fontSize: '0.78rem' }}>
                  {customQrCopied ? '✓ הועתק' : '📋 העתק כרטיס (שם + QR)'}
                </button>{' '}
                <button onClick={async () => {
                  const blob = await buildQrShareImage(qrUrl, customCard.name || '', customCard.title || '', customCard.avatar_url, customCard.avatar_pos, customCard.avatar_zoom).catch(() => null);
                  if (!blob) return;
                  await shareOrDownloadImage(blob, `card-${customCard.name || 'card'}.png`);
                }} style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.78rem' }}>
                  <PreviewIconWhatsApp size={14} /> שלח (עם QR)
                </button>
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  if (!card) return <div><HeaderRow onBack={onBack} />{picker}<div style={{ color: '#9ca3af', padding: 20 }}>טוען...</div></div>;

  const publicUrl = card.card_slug ? `https://mabat.communityltd.co.il/card/worker/${card.card_slug}` : null;

  return (
    <div>
      <HeaderRow onBack={onBack} />
      {picker}
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ width: 110, height: 110, borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', border: '1px solid #e5e7eb' }}>
            {card.avatar_url
              ? <img src={card.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: avatarTransform(card.avatar_pos, card.avatar_zoom) }} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem' }}>👤</div>}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ cursor: 'pointer', fontSize: '0.78rem', color: '#1d4ed8' }}>
              {card.avatar_url ? 'החלף תמונה' : '+ תמונה'}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => { const f = e.target.files[0]; if (f) uploadAvatar(f); }} />
            </label>
            {card.avatar_url && (
              <button onClick={() => {
                // avatar_pos stores a translate() offset (0,0 = centered); a
                // leftover default "50% 50%" also maps to centered.
                const raw = card.avatar_pos && card.avatar_pos !== '50% 50%' ? card.avatar_pos : '0% 0%';
                const [x, y] = raw.replace(/%/g, '').split(' ').map(Number);
                setRepositioning({ avatar_url: card.avatar_url, x: isNaN(x) ? 0 : x, y: isNaN(y) ? 0 : y, zoom: card.avatar_zoom ? Number(card.avatar_zoom) : 1 });
              }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#6b7280' }}>✏️ מיקום/זום</button>
            )}
          </div>
        </div>

        <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>תפקיד לתצוגה</label>
        <input style={inp} placeholder="לדוגמה: מנהל פרויקט" value={form.display_title} onChange={e => setForm(f => ({ ...f, display_title: e.target.value }))} />

        <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>טלפון בכרטיס</label>
        <input style={inp} placeholder="נפרד מהטלפון להתחברות" value={form.card_phone} onChange={e => setForm(f => ({ ...f, card_phone: e.target.value }))} />
        <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>אימייל בכרטיס</label>
        <input style={inp} value={form.card_email} onChange={e => setForm(f => ({ ...f, card_email: e.target.value }))} />

        <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: 3, marginTop: 6 }}>קישורים</label>
        {SOCIAL_LINK_TYPES.map(({ type, label }) => (
          <input key={type} style={inp} placeholder={label} value={form.social_links[type] || ''}
            onChange={e => setForm(f => ({ ...f, social_links: { ...f.social_links, [type]: e.target.value } }))} />
        ))}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ fontSize: '0.85rem' }}>{saving ? 'שומר...' : 'שמור'}</button>
          <button className="btn btn-secondary" onClick={() => setPreviewing(true)} style={{ fontSize: '0.85rem' }}>👁 תצוגה מקדימה</button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#6b7280', marginBottom: 10 }}>קישור ציבורי</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!card.card_enabled} disabled={toggling} onChange={e => toggleEnabled(e.target.checked)} />
          <span style={{ fontSize: '0.85rem' }}>הפעל כרטיס ציבורי (נגיש בלי התחברות)</span>
        </label>
        {card.card_enabled && publicUrl && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <input readOnly value={publicUrl} style={{ ...inp, marginBottom: 0, direction: 'ltr', fontSize: '0.78rem' }} onFocus={e => e.target.select()} />
              <button onClick={() => { navigator.clipboard.writeText(publicUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: linkCopied ? '#f0fdf4' : 'white', color: linkCopied ? '#15803d' : '#374151', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                {linkCopied ? '✓ הועתק' : 'העתק'}
              </button>
              {/* wa.me with no phone number opens WhatsApp's own contact
                  picker, so the sender chooses who to send the link to. */}
              <a href={`https://wa.me/?text=${encodeURIComponent(publicUrl)}`} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap', textDecoration: 'none' }}>
                <PreviewIconWhatsApp size={14} /> שלח
              </a>
            </div>
            <img src={`https://mabat.communityltd.co.il/api/portal/card/worker/${card.card_slug}/qr`} alt="QR" style={{ width: 140, height: 140, display: 'block' }} />
            {/* Sent as a link, WhatsApp's own preview-thumbnail compression
                makes the QR too blurry to scan — copying the actual image
                (not the link) and pasting it as a photo keeps it scannable. */}
            <button onClick={async () => {
              const qrUrl = `https://mabat.communityltd.co.il/api/portal/card/worker/${card.card_slug}/qr`;
              const fullName = `${card.workerFirstName || ''} ${card.workerLastName || ''}`.trim();
              try {
                const blob = await buildQrShareImage(qrUrl, fullName, form.display_title, card.avatar_url, card.avatar_pos, card.avatar_zoom);
                await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                setQrCopied(true); setTimeout(() => setQrCopied(false), 2000);
              } catch {
                try {
                  const blob = await buildQrShareImage(qrUrl, fullName, form.display_title, card.avatar_url, card.avatar_pos, card.avatar_zoom);
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `card-${card.workerFirstName || 'card'}.png`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                } catch { downloadCrossOriginFile(qrUrl, `qr-${card.workerFirstName || 'card'}.png`); }
              }
            }} style={{ marginTop: 8, padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: qrCopied ? '#f0fdf4' : 'white', color: qrCopied ? '#15803d' : '#374151', cursor: 'pointer', fontSize: '0.78rem' }}>
              {qrCopied ? '✓ הועתק' : '📋 העתק כרטיס (שם + QR)'}
            </button>{' '}
            {/* wa.me only pre-fills text, never an image — the Web Share API
                (mobile only) is the one way to hand WhatsApp an actual image
                file and let the user pick who to send it to. */}
            <button onClick={async () => {
              const qrUrl = `https://mabat.communityltd.co.il/api/portal/card/worker/${card.card_slug}/qr`;
              const fullName = `${card.workerFirstName || ''} ${card.workerLastName || ''}`.trim();
              const blob = await buildQrShareImage(qrUrl, fullName, form.display_title, card.avatar_url, card.avatar_pos, card.avatar_zoom).catch(() => null);
              if (!blob) return;
              await shareOrDownloadImage(blob, `card-${card.workerFirstName || 'card'}.png`);
            }} style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.78rem' }}>
              <PreviewIconWhatsApp size={14} /> שלח (עם QR)
            </button>
          </>
        )}
      </div>

      {repositioning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setRepositioning(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, padding: 20, width: 260 }}>
            <DragReposition repositioning={repositioning} setRepositioning={setRepositioning} onSave={saveReposition} onCancel={() => setRepositioning(null)} />
          </div>
        </div>
      )}

      {previewing && <WorkerCardPreviewModal card={card} form={form} onClose={() => setPreviewing(false)} />}
    </div>
  );
}

export default function DigitalCardEditor() {
  const { projectName: rawProjectName } = useParams();
  const projectName = decodeURIComponent(rawProjectName);
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8' }}>
      <Navbar title={`כרטיס ביקור דיגיטלי — ${projectName}`} breadcrumb="פרויקטים > כרטיס ביקור דיגיטלי" />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>
        <DigitalCardEditorInner projectName={projectName} onBack={() => navigate(`/project-hub/${encodeURIComponent(projectName)}`)} />
      </div>
    </div>
  );
}
